// Zoom / pan / fullscreen controller for a page image inside a scrolling
// viewport. Ported from toseftasite, which had the more careful of the two
// implementations; taanticMidrashimDig's fixed zoom-step version is replaced
// by this.
//
// Zoom is applied as a real layout width (a percentage, so it's relative to
// the container and needs no manual pixel math), NOT a CSS transform.
// transform:scale() only visually enlarges the element -- it doesn't change
// its layout box, and browsers largely don't grow a scrolling ancestor's
// horizontal scrollable-overflow region for that (confirmed: scrollWidth
// stayed pinned to clientWidth at any zoom level under transform, while
// scrollHeight DID grow -- an inconsistent, unreliable asymmetry). An actual
// width resize makes native scrollbars / trackpad / drag-panning work
// correctly in both directions, with no special-casing.

export const ZOOM_DEFAULTS = {
    min: 0.4,
    max: 6,
    focus: 3,      // zoom applied when a word is focused
    step: 1.25,    // per button press
    wheelStep: 1.08,
};

export function createZoomController(viewport, opts = {}) {
    const cfg = { ...ZOOM_DEFAULTS, ...opts };
    let zoom = 1;
    let wrapEl = null, boxEl = null;

    function clamp(z) { return Math.min(cfg.max, Math.max(cfg.min, z)); }

    function applyZoomWidth() {
        if (!wrapEl) return;
        wrapEl.style.width = (zoom * 100) + '%';
    }

    // A Hebrew page computes direction:rtl, so the viewport's scrollLeft
    // follows the "negative" RTL scrolling model every modern browser now
    // implements: 0 = home = viewport's LEFT edge sits at physical content-X
    // (scrollWidth - clientWidth); more negative reveals content further
    // toward physical-X 0. offsetLeft/offsetTop are always physical
    // (direction-independent), so converting a desired physical
    // viewport-left-edge X into scrollLeft needs this offset subtracted --
    // using the *positive* LTR-style formula directly (as a first version of
    // this code did) computes wildly out-of-range targets that silently clamp
    // to 0, which looked like "horizontal scroll/centering does nothing".
    function physicalLeftToScrollLeft(physicalX) {
        if (!isRtl()) return physicalX;
        return physicalX - (viewport.scrollWidth - viewport.clientWidth);
    }

    function isRtl() {
        return getComputedStyle(viewport).direction === 'rtl';
    }

    // Re-center the scroll position on the focused word's highlight box at the
    // current zoom. Since the box is positioned with PERCENTAGES against the
    // wrap, offsetLeft/Top/Width/Height already reflect its real, current
    // on-screen pixel position at whatever width the wrap now has -- no
    // separate *zoom factor needed (unlike the old transform approach).
    function centerOnBox(behavior = 'smooth') {
        if (!wrapEl || !boxEl) return;
        applyZoomWidth();
        const boxCenterX = boxEl.offsetLeft + boxEl.offsetWidth / 2;
        const targetLeft = physicalLeftToScrollLeft(boxCenterX - viewport.clientWidth / 2);
        const targetTop = boxEl.offsetTop + boxEl.offsetHeight / 2 - viewport.clientHeight / 2;
        viewport.scrollTo({ left: targetLeft, top: targetTop, behavior });
    }

    function setZoomAtPoint(newZoom, clientX, clientY) {
        if (!wrapEl) return;
        newZoom = clamp(newZoom);
        const rect = viewport.getBoundingClientRect();
        const cx = clientX - rect.left, cy = clientY - rect.top;
        // Work in fractions of content width/height so the RTL offset only
        // has to be applied once, at the very end, when writing scrollLeft.
        const oldScrollWidth = viewport.scrollWidth;
        const physX = isRtl()
            ? (oldScrollWidth - viewport.clientWidth + viewport.scrollLeft) + cx
            : viewport.scrollLeft + cx;
        const fracX = physX / oldScrollWidth;
        const fracY = (viewport.scrollTop + cy) / viewport.scrollHeight;
        zoom = newZoom;
        applyZoomWidth();
        viewport.scrollLeft = physicalLeftToScrollLeft(fracX * viewport.scrollWidth - cx);
        viewport.scrollTop = fracY * viewport.scrollHeight - cy;
    }

    function stepZoom(factor) {
        if (!wrapEl) return;
        zoom = clamp(zoom * factor);
        if (boxEl) centerOnBox();
        else applyZoomWidth();
    }

    function reset() {
        zoom = 1;
        if (boxEl) centerOnBox();
        else if (wrapEl) {
            applyZoomWidth();
            viewport.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
        }
    }

    // Ctrl/pinch + wheel zooms at the pointer (trackpad pinch is reported as a
    // wheel event with ctrlKey by every major browser); plain wheel keeps its
    // native scroll/pan behavior untouched.
    function onWheel(e) {
        if (!e.ctrlKey || !wrapEl) return;
        e.preventDefault();
        setZoomAtPoint(zoom * (e.deltaY < 0 ? cfg.wheelStep : 1 / cfg.wheelStep), e.clientX, e.clientY);
    }

    let dragging = false, lastX = 0, lastY = 0;
    function onMouseDown(e) {
        if (!wrapEl || e.button !== 0) return;
        dragging = true;
        lastX = e.clientX; lastY = e.clientY;
        viewport.classList.add('mvc-dragging');
        e.preventDefault();
    }
    function onMouseMove(e) {
        if (!dragging) return;
        viewport.scrollLeft -= (e.clientX - lastX);
        viewport.scrollTop -= (e.clientY - lastY);
        lastX = e.clientX; lastY = e.clientY;
    }
    function onMouseUp() {
        dragging = false;
        viewport.classList.remove('mvc-dragging');
    }

    viewport.addEventListener('wheel', onWheel, { passive: false });
    viewport.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return {
        // Called by the panel after each re-render, with the new elements
        // (box may be null when the word has no recognised box).
        attach(wrap, box) {
            wrapEl = wrap; boxEl = box;
        },
        detach() { wrapEl = null; boxEl = null; },
        focus() {
            zoom = cfg.focus;
            centerOnBox();
        },
        get zoom() { return zoom; },
        centerOnBox,
        setZoomAtPoint,
        zoomIn: () => stepZoom(cfg.step),
        zoomOut: () => stepZoom(1 / cfg.step),
        reset,
        // Fullscreen toggles the Fullscreen API directly on the viewport rather
        // than moving the image into a separate overlay -- the element stays
        // put in the DOM, so the zoom/pan/drag listeners already bound to it
        // keep working unchanged while it's promoted to fill the screen.
        toggleFullscreen() {
            if (document.fullscreenElement === viewport) document.exitFullscreen();
            else viewport.requestFullscreen();
        },
        isFullscreen() { return document.fullscreenElement === viewport; },
        destroy() {
            viewport.removeEventListener('wheel', onWheel);
            viewport.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        },
    };
}
